/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Row, Col, Spin, DatePicker, Select, Button, Radio, Table } from 'antd';
import { DashboardOutlined, LineChartOutlined, SafetyOutlined, CheckCircleOutlined, BarChartOutlined, AppstoreOutlined, TeamOutlined, ShoppingOutlined, WarningOutlined, LoadingOutlined, PieChartOutlined } from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axiosConfig from '../../services/axiosConfig';
import moment from 'moment';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// ========== CHART COMPONENTS ==========

const DefectTrendChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { date: 'Feb 3', porosity: 3, lof: 1, slag: 2, cracks: 0, ip: 1 },
    { date: 'Feb 4', porosity: 4, lof: 2, slag: 1, cracks: 0, ip: 2 },
    { date: 'Feb 5', porosity: 2, lof: 1, slag: 3, cracks: 1, ip: 0 },
    { date: 'Feb 6', porosity: 5, lof: 3, slag: 2, cracks: 0, ip: 1 },
    { date: 'Feb 7', porosity: 3, lof: 2, slag: 1, cracks: 0, ip: 2 },
    { date: 'Feb 8', porosity: 4, lof: 1, slag: 2, cracks: 1, ip: 1 },
    { date: 'Feb 9', porosity: 2, lof: 2, slag: 3, cracks: 0, ip: 0 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  return (
    <Card className="dashboard-card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(255, 77, 79, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LineChartOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Defect Trends (Last 7 Days)</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="date" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
          <Legend wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
          <Line type="monotone" dataKey="porosity" stroke="#8884d8" strokeWidth={2} name="Porosity" dot={{ fill: '#8884d8', r: 4 }} />
          <Line type="monotone" dataKey="lof" stroke="#82ca9d" strokeWidth={2} name="Lack of Fusion" dot={{ fill: '#82ca9d', r: 4 }} />
          <Line type="monotone" dataKey="slag" stroke="#ffc658" strokeWidth={2} name="Slag Inclusion" dot={{ fill: '#ffc658', r: 4 }} />
          <Line type="monotone" dataKey="cracks" stroke="#ff4d4f" strokeWidth={2} name="Cracks" dot={{ fill: '#ff4d4f', r: 4 }} />
          <Line type="monotone" dataKey="ip" stroke="#a4de6c" strokeWidth={2} name="Incomplete Penetration" dot={{ fill: '#a4de6c', r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

const AcceptanceRateChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { date: 'Mon', rtRate: 92, utRate: 95 },
    { date: 'Tue', rtRate: 88, utRate: 91 },
    { date: 'Wed', rtRate: 91, utRate: 94 },
    { date: 'Thu', rtRate: 93, utRate: 96 },
    { date: 'Fri', rtRate: 89, utRate: 92 },
    { date: 'Sat', rtRate: 94, utRate: 97 },
    { date: 'Sun', rtRate: 91, utRate: 93 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  return (
    <Card className="dashboard-card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(82, 196, 26, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Acceptance Rate Trends</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorRt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#52c41a" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorUt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="date" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <YAxis stroke="rgba(255,255,255,0.65)" domain={[0, 100]} tickFormatter={(value) => `${value}%`} style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} formatter={(value) => `${value}%`} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Area type="monotone" dataKey="rtRate" stroke="#52c41a" strokeWidth={2} fill="url(#colorRt)" name="RT Acceptance Rate" dot={{ fill: '#52c41a', r: 4 }} />
          <Area type="monotone" dataKey="utRate" stroke="#1890ff" strokeWidth={2} fill="url(#colorUt)" name="UT Acceptance Rate" dot={{ fill: '#1890ff', r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
};

const WelderPerformanceChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { welder: 'Abijith', accepted: 46, rejected: 2 },
    { welder: 'Devaraj', accepted: 31, rejected: 2 },
    { welder: 'Madhaudhan', accepted: 30, rejected: 1 },
    { welder: 'Kumar', accepted: 28, rejected: 3 },
    { welder: 'Ravi', accepted: 25, rejected: 1 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  return (
    <Card className="dashboard-card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(114, 46, 209, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TeamOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Welder Performance</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="welder" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} angle={-45} textAnchor="end" height={80} />
          <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Bar dataKey="accepted" fill="#52c41a" name="Accepted" radius={[8, 8, 0, 0]} />
          <Bar dataKey="rejected" fill="#ff4d4f" name="Rejected" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const DefectDistributionChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { name: 'Porosity', value: 156 },
    { name: 'Slag Inclusion', value: 112 },
    { name: 'Lack of Fusion', value: 67 },
    { name: 'Incomplete Penetration', value: 43 },
    { name: 'Cracks', value: 15 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  const COLORS = ['#8884d8', '#ffc658', '#82ca9d', '#ff4d4f', '#a4de6c'];
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: '14px', fontWeight: 600 }}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };
  return (
    <Card className="dashboard-card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(250, 173, 20, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PieChartOutlined style={{ color: '#faad14', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Defect Distribution</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={100} fill="#8884d8" dataKey="value">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            wrapperStyle={{ zIndex: 1000 }}
            contentStyle={{
              backgroundColor: '#2d3035',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: '#ffffff',
              padding: '12px 16px',
              fontSize: '14px',
              minWidth: '140px'
            }}
            labelStyle={{ color: '#ffffff' }}
            itemStyle={{ color: '#ffffff' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0];
              const name = item.name ?? item.payload?.name ?? '';
              const value = item.value ?? item.payload?.value ?? 0;
              const total = chartData.reduce((s, d) => s + (d.value || 0), 0);
              const pct = total ? ((Number(value) / total) * 100).toFixed(1) : '0';
              return (
                <div className="defect-distribution-tooltip" style={{ color: '#ffffff', fontSize: '14px', fontWeight: 500, background: 'transparent' }}>
                  <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>{name}</div>
                  <div style={{ color: '#e8e8e8', fontSize: '13px' }}>{value} ({pct}%)</div>
                </div>
              );
            }}
          />
          <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', display: 'flex', justifyContent: 'space-around', gap: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px' }}>Total Defects</div>
          <div style={{ color: '#fff', fontSize: '24px', fontWeight: 600 }}>{chartData.reduce((sum, item) => sum + item.value, 0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px' }}>Most Common</div>
          <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{chartData[0]?.name || 'N/A'}</div>
        </div>
      </div>
    </Card>
  );
};

// Removed: ProductivityComparisonChart — no backend data for planned vs actual

// API may return { value, percentage } for some metrics; ensure we render a primitive
const toDisplayValue = (v) => {
  if (v == null) return 0;
  if (typeof v === 'object' && v !== null && 'value' in v) return v.value;
  return typeof v === 'number' ? v : Number(v) || 0;
};

// Date range helpers for presets
const getRangeByPreset = (preset) => {
  const end = moment().endOf('day');
  let start;
  switch (preset) {
    case 'week': start = moment().subtract(1, 'week').startOf('day'); break;
    case 'month': start = moment().subtract(1, 'month').startOf('day'); break;
    case 'year': start = moment().subtract(1, 'year').startOf('day'); break;
    default: start = moment().subtract(1, 'month').startOf('day');
  }
  return [start, end];
};

const DashboardLog = ({
  shift = 'day',
  dprLogId,
  sowId
}) => {
  const [overviewPeriod, setOverviewPeriod] = useState('Monthly');
  const [bothShift, setBothShift] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  // Global filters (wireframe header)
  const [dateRangePreset, setDateRangePreset] = useState('month'); // 'custom' | 'week' | 'month' | 'year'
  const [customDateRange, setCustomDateRange] = useState(() => getRangeByPreset('month'));
  const [shiftFilter, setShiftFilter] = useState('all'); // 'all' | 'day' | 'night'
  const [sectionFilter, setSectionFilter] = useState('welding'); // 'welding' | 'ndt' | 'production'
  const [projectFilter, setProjectFilter] = useState('current'); // 'current' | projectId when multiple projects supported

  // Fetch dashboard metrics (respects global filters: date range, shift, section)
  const fetchDashboardMetrics = useCallback(async () => {
    if (!dprLogId) return;

    setIsLoadingMetrics(true);
    try {
      const shiftValue = shiftFilter === 'all' ? 'both' : (shiftFilter === 'day' ? 'day' : 'night');
      const [start, end] = dateRangePreset === 'custom' && customDateRange?.[0] && customDateRange?.[1]
        ? customDateRange
        : getRangeByPreset(dateRangePreset);

      const response = await axiosConfig.get(`/dpr-dashboard/metrics`, {
        params: {
          dprLogId,
          shift: shiftValue,
          fromDate: start ? moment(start).format('YYYY-MM-DD') : undefined,
          toDate: end ? moment(end).format('YYYY-MM-DD') : undefined,
          section: sectionFilter
        }
      });

      if (response.data && response.data.data) {
        setDashboardMetrics(response.data.data);
        console.log('Dashboard metrics fetched:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [dprLogId, shift, shiftFilter, dateRangePreset, customDateRange, sectionFilter]);

  // Fetch metrics when component mounts or when dependencies change
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  const dashboardSections = [
    { key: 'productivity', title: 'Productivity (Weld Log)', fullWidth: true },
    { key: 'safety', title: 'Safety', fullWidth: true },
    { key: 'quality', title: 'Quality', fullWidth: true },
    { key: 'pipeTally', title: 'Pipe Tally', fullWidth: true },
    { key: 'personnel', title: 'Personnel', fullWidth: true },
    { key: 'productionImpact', title: 'Production Impact', fullWidth: true },
    { key: 'consumables', title: 'Consumables', fullWidth: true }
  ];

  const effectiveDateRange = dateRangePreset === 'custom' ? customDateRange : getRangeByPreset(dateRangePreset);

  return (
    <div className="dpr-dashboard-log" style={{ padding: '24px', backgroundColor: '#1a1d24', minHeight: '400px', borderRadius: '8px' }}>
      <style>
        {`
          .dashboard-accordion .ant-collapse-item {
            background-color: transparent !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 8px !important;
            margin-bottom: 16px !important;
          }
          .dashboard-accordion .ant-collapse-header {
            background-color: rgba(255, 255, 255, 0.05) !important;
            border-radius: 8px !important;
            padding: 16px 20px !important;
            color: #fff !important;
          }
          .dashboard-accordion .ant-collapse-header:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
          }
          .dashboard-accordion .ant-collapse-content {
            background-color: transparent !important;
            border: none !important;
          }
          .dashboard-accordion .ant-collapse-content-box {
            padding: 20px !important;
          }
          .dashboard-card {
            border-radius: 8px !important;
            background-color: rgba(255, 255, 255, 0.05) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          .dashboard-card .ant-card-body {
            padding: 20px !important;
            background: transparent !important;
          }
          .dashboard-card-equal-height {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-card-equal-height .ant-card-body {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-select .ant-select-selector {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
            color: #fff !important;
            border-radius: 4px !important;
          }
          .dashboard-select .ant-select-selection-item {
            color: #fff !important;
          }
          .dashboard-select .ant-select-arrow {
            color: rgba(255, 255, 255, 0.65) !important;
          }
          .dashboard-select:hover .ant-select-selector {
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
          .dashboard-select.ant-select-focused .ant-select-selector {
            border-color: rgba(255, 77, 79, 0.6) !important;
            box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
          }
          /* Light theme overrides */
          .light-theme h2[style*="color: rgb(255, 255, 255)"],
          .light-theme h2[style*="color:#fff"],
          .light-theme h4[style*="color: rgb(255, 255, 255)"],
          .light-theme h4[style*="color:#fff"],
          .light-theme span[style*="color: rgb(255, 255, 255)"],
          .light-theme span[style*="color:#fff"],
          .light-theme div[style*="color: rgb(255, 255, 255)"],
          .light-theme div[style*="color:#fff"],
          .light-theme label[style*="color: rgb(255, 255, 255)"],
          .light-theme label[style*="color:#fff"] {
            color: #000 !important;
          }
          .light-theme .dashboard-card {
            background-color: rgba(0, 0, 0, 0.03) !important;
            border-color: rgba(0, 0, 0, 0.15) !important;
          }
          /* Override all rgba white colors in light theme */
          .light-theme div[style*="color: rgba(255, 255, 255"],
          .light-theme span[style*="color: rgba(255, 255, 255"] {
            color: rgba(0, 0, 0, 0.75) !important;
          }
          /* Input and textarea text and placeholder in light theme */
          .light-theme input[style*="color: rgb(255, 255, 255)"],
          .light-theme input[style*="color:#fff"],
          .light-theme textarea[style*="color: rgb(255, 255, 255)"],
          .light-theme textarea[style*="color:#fff"],
          .light-theme .ant-input[style*="color: rgb(255, 255, 255)"],
          .light-theme .ant-input[style*="color:#fff"] {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme input::placeholder,
          .light-theme textarea::placeholder,
          .light-theme .ant-input::placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .ant-input-number-input {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          /* Select component text in light theme */
          .light-theme .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .ant-select-selection-placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .dashboard-select .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .dashboard-select .ant-select-selector {
            background-color: rgba(0, 0, 0, 0.05) !important;
            border-color: rgba(0, 0, 0, 0.15) !important;
          }
          .light-theme .dashboard-select:hover .ant-select-selector {
            border-color: rgba(0, 0, 0, 0.25) !important;
          }
          .light-theme .dashboard-select.ant-select-focused .ant-select-selector {
            background-color: rgba(0, 0, 0, 0.08) !important;
            border-color: rgba(255, 77, 79, 0.6) !important;
          }
          .dpr-dashboard-log .recharts-text {
            fill: rgba(255, 255, 255, 0.92) !important;
          }
          .dpr-dashboard-log .recharts-legend-item-text {
            color: rgba(255, 255, 255, 0.92) !important;
          }
          .dpr-dashboard-log .recharts-cartesian-axis-tick-value {
            fill: rgba(255, 255, 255, 0.9) !important;
          }
          .dpr-dashboard-log .recharts-tooltip-wrapper,
          .dpr-dashboard-log .recharts-default-tooltip {
            color: #ffffff !important;
            background: #2d3035 !important;
          }
          .dpr-dashboard-log .defect-distribution-tooltip,
          .dpr-dashboard-log .defect-distribution-tooltip * {
            color: #ffffff !important;
          }
          .dpr-dashboard-log .consumables-trend-tooltip,
          .dpr-dashboard-log .consumables-trend-tooltip * {
            color: #ffffff !important;
          }
          .dpr-dashboard-log .ant-picker,
          .dpr-dashboard-log .ant-select-selector,
          .dpr-dashboard-log .ant-radio-button-wrapper {
            background: rgba(255,255,255,0.08) !important;
            border-color: rgba(255,255,255,0.2) !important;
            color: #fff !important;
          }
          .dpr-dashboard-log .ant-picker-input input { color: #fff !important; }
          .dpr-dashboard-log .ant-select-selection-item { color: #fff !important; }
          .dpr-dashboard-log .ant-radio-button-wrapper-checked { background: #ff4d4f !important; border-color: #ff4d4f !important; }
          .dpr-dashboard-log .ant-table { background: transparent !important; color: #fff !important; }
          .dpr-dashboard-log .ant-table-thead > tr > th { background: rgba(255,255,255,0.1) !important; color: rgba(255,255,255,0.9) !important; border-color: rgba(255,255,255,0.15) !important; }
          .dpr-dashboard-log .ant-table-tbody > tr > td { border-color: rgba(255,255,255,0.1) !important; }
          .dpr-dashboard-log .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.05) !important; }
          .dpr-dashboard-log .ant-table-placeholder { color: rgba(255,255,255,0.5) !important; }
        `}
      </style>

      {/* 🧭 GLOBAL CONTROLS – TOP BAR (applies to all charts below) */}
      <Card className="dashboard-card" style={{ marginBottom: '20px' }}>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase' }}>Filters · Apply to all charts</div>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={24} md={24} lg={24}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginRight: '4px' }}>Date range:</span>
              <Radio.Group
                value={dateRangePreset}
                onChange={(e) => setDateRangePreset(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="custom">Custom</Radio.Button>
                <Radio.Button value="week">Week</Radio.Button>
                <Radio.Button value="month">Month</Radio.Button>
                <Radio.Button value="year">Year</Radio.Button>
              </Radio.Group>
              {dateRangePreset === 'custom' && (
                <RangePicker
                  value={customDateRange && customDateRange[0] && customDateRange[1] ? [moment(customDateRange[0]), moment(customDateRange[1])] : null}
                  onChange={(dates) => setCustomDateRange(dates && dates[0] && dates[1] ? [dates[0].toDate(), dates[1].toDate()] : getRangeByPreset('month'))}
                  size="small"
                  allowClear={false}
                  style={{ marginLeft: '8px' }}
                />
              )}
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginLeft: '8px' }}>
                {effectiveDateRange && effectiveDateRange[0] && effectiveDateRange[1]
                  ? `${moment(effectiveDateRange[0]).format('DD MMM YYYY')} – ${moment(effectiveDateRange[1]).format('DD MMM YYYY')}`
                  : ''}
              </span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', minWidth: '70px' }}>Project:</span>
              <Select
                value={projectFilter}
                onChange={setProjectFilter}
                size="small"
                style={{ width: '140px' }}
                options={[{ value: 'current', label: 'Current project' }]}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', minWidth: '70px' }}>Shift:</span>
              <Select
                value={shiftFilter}
                onChange={setShiftFilter}
                size="small"
                style={{ width: '100px' }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'day', label: 'Day' },
                  { value: 'night', label: 'Night' }
                ]}
              />
            </div>
          </Col>
          {/* Section filter hidden for now */}
          {false && (
          <Col xs={24} sm={12} md={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', minWidth: '70px' }}>Section:</span>
              <Select
                value={sectionFilter}
                onChange={setSectionFilter}
                size="small"
                style={{ width: '140px' }}
                options={[
                  { value: 'welding', label: 'Welding' },
                  { value: 'ndt', label: 'NDT' },
                  { value: 'production', label: 'Production' }
                ]}
              />
            </div>
          </Col>
          )}
          <Col xs={24} sm={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px' }}>
            <Button size="small" type="default" style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>Export PDF</Button>
            <Button size="small" type="default" style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>Export Excel</Button>
          </Col>
        </Row>
      </Card>

      {/* Header Section */}
      <div style={{ marginBottom: '24px', marginTop: '-23px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <DashboardOutlined style={{ color: '#fff', fontSize: '18px' }} />
            </div>
            <Title level={2} style={{ margin: 0, color: '#fff', fontSize: '24px', fontWeight: 600 }}>
              Dashboard
            </Title>
          </div>
        </div>
        {/* Data sources: all DPR sections and WELD LOG fields → dashboard (welding offshore/onshore) */}
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.12)'
        }}>
          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Where this data comes from (all DPR sections)</div>
          <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: 1.5 }}>
            <strong>Weld Log</strong> (Joint No, Welder ID, Weld Status Accept/Reject, Blow Through, Station 1 Cut Out, RT Accepted, Repair, Repair Accepted, RT Cut Out, Defect Type, Indication Info – Start/End Location, Depth, Length, Height, Defect Accepted, Customer/CRCE/Repair Comments) → Defect trends, Acceptance rate, Welder performance, Productivity & Quality cards. <strong>Safety</strong> → Safety card · <strong>Quality</strong> → Quality card · <strong>Downtime</strong> → Production Impact · <strong>Consumables</strong> → Consumables card · <strong>Personnel</strong> → Personnel card · <strong>Pipe tally</strong> → Pipe tally card.
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
            Use these graphs and metrics for project KPIs, weld acceptance tracking, defect trends, and reporting for offshore and onshore welding projects — not just display.
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoadingMetrics ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#ff4d4f' }} spin />}
            size="large"
          />
          <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '16px', fontWeight: 400 }}>
            Loading Dashboard...
          </div>
        </div>
      ) : (
        <>
            {/* 🔹 SECTION 1: PROJECT HEALTH KPIs */}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}> Project health KPIs</div>
            <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
              <Col xs={12} sm={8} lg={4} xl={4}>
                <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Welds Logged</div>
                  <div style={{ color: '#fff', fontSize: '28px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.productivity?.totalWelds)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Joints welded at Station 1</div>
                </Card>
              </Col>
              <Col xs={12} sm={8} lg={4} xl={4}>
                <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Acceptance %</div>
                  {(() => {
                    const rate = Number(dashboardMetrics?.productivity?.acceptanceRate) || 0;
                    const color = rate >= 95 ? '#52c41a' : rate >= 85 ? '#faad14' : '#ff4d4f';
                    return <div style={{ color, fontSize: '28px', fontWeight: 700 }}>{`${rate.toFixed(1)}%`}</div>;
                  })()}
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>RT acceptance rate</div>
                </Card>
              </Col>
              <Col xs={12} sm={8} lg={4} xl={4}>
                <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>RT Completed</div>
                  <div style={{ color: '#1890ff', fontSize: '28px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.productivity?.rtAccepted)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>QA throughput</div>
                </Card>
              </Col>
              <Col xs={12} sm={8} lg={4} xl={4}>
                <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Cut Outs</div>
                  <div style={{ color: toDisplayValue(dashboardMetrics?.productivity?.cutoffs) > 0 ? '#ff4d4f' : '#fff', fontSize: '28px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.productivity?.cutoffs)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Joints removed</div>
                </Card>
              </Col>
              <Col xs={12} sm={8} lg={4} xl={4}>
                <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Total DPRs</div>
                  <div style={{ color: '#fff', fontSize: '28px', fontWeight: 700 }}>{dashboardMetrics?.dprCount != null ? toDisplayValue(dashboardMetrics.dprCount) : '-'}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Reports submitted</div>
                </Card>
              </Col>
            </Row>

            {/* 🔹 SECTION 2: PRODUCTIVITY OVERVIEW (Weld Log summary) */}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Productivity overview</div>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} lg={14}>
                <Card className="dashboard-card" style={{ marginBottom: 0 }}>
                  <Title level={5} style={{ margin: '0 0 16px 0', color: '#fff', fontSize: '14px' }}>Weld Log summary</Title>
                  {(() => {
                    const p = dashboardMetrics?.productivity;
                    const summaryData = [
                      { metric: 'Welded (Stn 1)', value: toDisplayValue(p?.totalWelds), fill: '#1890ff' },
                      { metric: 'RT Accepted', value: toDisplayValue(p?.rtAccepted), fill: '#52c41a' },
                      { metric: 'RT Rejected', value: toDisplayValue(p?.rtRejected), fill: '#ff4d4f' },
                      { metric: 'Repairs', value: toDisplayValue(p?.repairs), fill: '#faad14' },
                      { metric: 'Cut Outs', value: toDisplayValue(p?.cutoffs), fill: '#ff4d4f' },
                      { metric: 'Blow Through', value: toDisplayValue(p?.blowThrough), fill: '#722ed1' }
                    ];
                    return (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={summaryData} layout="vertical" margin={{ left: 90, right: 20, top: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                          <YAxis type="category" dataKey="metric" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} width={90} />
                          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                          <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                            {summaryData.map((entry, idx) => <Cell key={`wl-${idx}`} fill={entry.fill} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>First Joint</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{dashboardMetrics?.productivity?.firstJoint || '-'}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Last Joint</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{dashboardMetrics?.productivity?.lastJoint || '-'}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Acceptance Rate</div>
                      <div style={{ color: '#52c41a', fontSize: '14px', fontWeight: 600 }}>{`${(Number(dashboardMetrics?.productivity?.acceptanceRate) || 0).toFixed(1)}%`}</div>
                    </div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginTop: '8px' }}>From: Weld Log (Joint No, Welder ID, Status, RT, Repair, Cut Out, Blow Through)</div>
                </Card>
              </Col>
              <Col xs={24} lg={10}>
                <Card className="dashboard-card" style={{ marginBottom: 0 }}>
                  <Title level={5} style={{ margin: '0 0 16px 0', color: '#fff', fontSize: '14px' }}>DPR submission trend</Title>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={dashboardMetrics?.dprSubmissionTrend || [{ date: 'Mon', count: 2 }, { date: 'Tue', count: 3 }, { date: 'Wed', count: 1 }, { date: 'Thu', count: 4 }, { date: 'Fri', count: 2 }]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="date" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                      <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                      <Line type="monotone" dataKey="count" stroke="#1890ff" strokeWidth={2} name="DPR count" dot={{ fill: '#1890ff', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '8px' }}>Missing day = missing DPR = missing data</div>
                </Card>
              </Col>
            </Row>

            {/* 🔹 SECTION 3: QUALITY CONTROL CENTER */}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Quality control center</div>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} lg={8}>
                <Card className="dashboard-card" style={{ marginBottom: 0 }}>
                  <Title level={5} style={{ margin: '0 0 16px 0', color: '#fff', fontSize: '14px' }}>Weld status distribution</Title>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={dashboardMetrics?.weldStatusDistribution || [{ name: 'Accepted', value: 85 }, { name: 'Accept w/ Indication', value: 10 }, { name: 'Rejected', value: 5 }]} innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value" nameKey="name">
                        <Cell fill="#52c41a" /><Cell fill="#faad14" /><Cell fill="#ff4d4f" />
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                      <Legend wrapperStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '8px' }}>Accept vs reject proportion</div>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card className="dashboard-card" style={{ marginBottom: 0 }}>
                  <Title level={5} style={{ margin: '0 0 16px 0', color: '#fff', fontSize: '14px' }}>RT & repair outcome</Title>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={dashboardMetrics?.rtRepairOutcome || (() => { const p = dashboardMetrics?.productivity; return [{ metric: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) }, { metric: 'Repair Required', value: toDisplayValue(p?.repairs) }, { metric: 'Repair Accepted', value: toDisplayValue(p?.repairs) }, { metric: 'RT Cut Out', value: toDisplayValue(p?.cutoffs) }]; })()} layout="vertical" margin={{ left: 90 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                      <YAxis type="category" dataKey="metric" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} width={100} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                      <Bar dataKey="value" fill="#722ed1" name="Count" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '8px' }}>QA pipeline flow</div>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <WelderPerformanceChart data={dashboardMetrics?.welderPerformance || []} dataSource="Station reject rate · Welder accountability" />
              </Col>
            </Row>

            {/* 🔹 SECTION 4: DEFECT ANALYSIS & ROOT CAUSE */}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Defect analysis & root cause</div>
            <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
              <Col xs={24} lg={24}>
                <DefectTrendChart data={dashboardMetrics?.defectTrends || []} dataSource="Defect type frequency · Root cause" />
              </Col>
              <Col xs={24} lg={12}>
                <DefectDistributionChart data={dashboardMetrics?.defectDistribution || []} dataSource="Quality Entry & Weld Log (Indication Info – Defect Type)" />
              </Col>
              <Col xs={24} lg={12}>
                <AcceptanceRateChart data={dashboardMetrics?.acceptanceRates || []} dataSource="Productivity (Weld Log) – RT Accepted / Rejected" />
              </Col>
            </Row>

            <div style={{ height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '32px 0' }} />

            {/* Dashboard Grid (existing section cards) */}
          <Row gutter={[16, 16]}>
            {dashboardSections.map((item) => (
              <Col xs={24} lg={24} key={item.key}>
                <Card className="dashboard-card" style={{ width: '100%' }}>
              {item.title === 'Safety' ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(82, 196, 26, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(82, 196, 26, 0.5)' }}>
                      <SafetyOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>SAFETY</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Safety Entry</div>
                  </div>
                  <Row gutter={[24, 16]}>
                    <Col xs={24} lg={12}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Today&apos;s incidents by category</div>
                      {(() => {
                        const sampleSafety = [
                          { name: 'Fatal Accidents', value: 0 }, { name: 'LTIs', value: 0 }, { name: 'RWC', value: 0 },
                          { name: 'MTC', value: 1 }, { name: 'Near Misses', value: 3 }, { name: 'FAC', value: 2 },
                          { name: 'Environmental', value: 0 }, { name: 'Other Incidents', value: 1 }, { name: 'Observation Cards', value: 5 }
                        ];
                        const chartData = dashboardMetrics?.safetyMetrics?.entriesList?.length > 0
                          ? dashboardMetrics.safetyMetrics.entriesList.map(e => ({ name: e.name || e.keyName, value: toDisplayValue(e.todayValue) }))
                          : sampleSafety;
                        return (
                          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 28)}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 130, right: 20, top: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                              <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} width={125} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Bar dataKey="value" fill="#52c41a" name="Today" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>
                    <Col xs={24} lg={12}>
                      <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Metric</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Today</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>To Date</div>
                        </div>
                        {(dashboardMetrics?.safetyMetrics?.entriesList?.length > 0 ? dashboardMetrics.safetyMetrics.entriesList : [
                          { name: 'Fatal Accidents', todayValue: 0, toDateValue: 0 }, { name: 'LTIs', todayValue: 0, toDateValue: 2 },
                          { name: 'RWC', todayValue: 0, toDateValue: 1 }, { name: 'MTC', todayValue: 1, toDateValue: 5 },
                          { name: 'Near Misses', todayValue: 3, toDateValue: 18 }, { name: 'FAC', todayValue: 2, toDateValue: 12 },
                          { name: 'Environmental', todayValue: 0, toDateValue: 3 }, { name: 'Other Incidents', todayValue: 1, toDateValue: 7 },
                          { name: 'Observation Cards', todayValue: 5, toDateValue: 42 }
                        ]).map((entry, index, arr) => (
                          <div key={entry.id || entry.keyName || entry.name || index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '8px 16px', borderBottom: index < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{entry.name || entry.keyName}</div>
                            <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(entry.todayValue)}</div>
                            <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(entry.toDateValue)}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '16px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Incidents Today</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.safetyMetrics?.today) || 7}</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Incidents To Date</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.safetyMetrics?.toDate) || 90}</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Total Incidents</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.safetyMetrics?.total) || 90}</div></div>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Quality' ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(24, 144, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(24, 144, 255, 0.5)' }}>
                      <CheckCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>QUALITY</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Quality Entry & Weld Log (Defect Tracking, Station Rejects)</div>
                  </div>
                  <Row gutter={[24, 16]}>
                    <Col xs={24} lg={12}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Today vs To-Date by quality metric</div>
                      {(() => {
                        const sampleQuality = [
                          { name: 'High Low Reject', today: 2, toDate: 45 }, { name: 'Station Reject', today: 5, toDate: 73 },
                          { name: 'RT Repair', today: 1, toDate: 34 }, { name: 'RT Cut Off', today: 0, toDate: 27 }
                        ];
                        const chartData = dashboardMetrics?.qualityReport?.entriesList?.length > 0
                          ? dashboardMetrics.qualityReport.entriesList.map(e => ({ name: e.name || e.key, today: toDisplayValue(e.today), toDate: toDisplayValue(e.toDate) }))
                          : sampleQuality;
                        return (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 50 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} angle={-20} textAnchor="end" height={60} />
                              <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Legend wrapperStyle={{ color: '#fff' }} />
                              <Bar dataKey="today" fill="#1890ff" name="Today" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="toDate" fill="rgba(24,144,255,0.35)" name="To Date" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>
                    <Col xs={24} lg={12}>
                      <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Metric</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Today</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>To Date</div>
                        </div>
                        {(dashboardMetrics?.qualityReport?.entriesList?.length > 0 ? dashboardMetrics.qualityReport.entriesList : [
                          { name: 'High Low Reject', today: 2, toDate: 45 }, { name: 'Station Reject', today: 5, toDate: 73 },
                          { name: 'RT Repair', today: 1, toDate: 34 }, { name: 'RT Cut Off', today: 0, toDate: 27 }
                        ]).map((entry, index, arr) => (
                          <div key={entry.key || entry.name || index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '10px 16px', borderBottom: index < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{entry.name || entry.key}</div>
                            <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(entry.today)}</div>
                            <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(entry.toDate)}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '16px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Issues Today</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.qualityReport?.totalIssues) || 8}</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Issues To Date</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.qualityReport?.toDate) || 179}</div></div>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Productivity (Weld Log)' ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(139, 69, 19, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BarChartOutlined style={{ color: '#8B4513', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>PRODUCTIVITY</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Weld Log – Joint No, Welder ID, Weld Status, RT, Blow Through, Repair</div>
                  </div>
                  <Row gutter={[24, 16]}>
                    <Col xs={24} lg={12}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Weld Log breakdown</div>
                      {(() => {
                        const p = dashboardMetrics?.productivity;
                        const chartData = [
                          { metric: 'Welded (Stn 1)', value: toDisplayValue(p?.totalWelds) || 85, fill: '#1890ff' },
                          { metric: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) || 72, fill: '#52c41a' },
                          { metric: 'RT Rejected', value: toDisplayValue(p?.rtRejected) || 8, fill: '#ff4d4f' },
                          { metric: 'Repairs', value: toDisplayValue(p?.repairs) || 5, fill: '#faad14' },
                          { metric: 'Cut Outs', value: toDisplayValue(p?.cutoffs) || 2, fill: '#ff4d4f' },
                          { metric: 'Blow Through', value: toDisplayValue(p?.blowThrough) || 3, fill: '#722ed1' }
                        ];
                        return (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 100, right: 20, top: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                              <YAxis type="category" dataKey="metric" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} width={95} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                                {chartData.map((entry, idx) => <Cell key={`pr-${idx}`} fill={entry.fill} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>
                    <Col xs={24} lg={12}>
                      {(() => {
                        const p = dashboardMetrics?.productivity;
                        const rows = [
                          { label: 'Welded at Station 1', value: toDisplayValue(p?.totalWelds) || 85, color: '#fff' },
                          { label: 'RT Completed', value: toDisplayValue(p?.rtCompleted) || 80, color: '#fff' },
                          { label: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) || 72, color: '#52c41a' },
                          { label: 'RT Rejected', value: toDisplayValue(p?.rtRejected) || 8, color: '#ff4d4f' },
                          { label: 'Acceptance Rate', value: `${(Number(p?.acceptanceRate) || 90).toFixed(1)}%`, color: Number(p?.acceptanceRate) >= 95 ? '#52c41a' : '#ff4d4f' },
                          { label: 'Repairs', value: toDisplayValue(p?.repairs) || 5, color: '#faad14' },
                          { label: 'Cut Outs', value: toDisplayValue(p?.cutoffs) || 2, color: '#ff4d4f' },
                          { label: 'Blow Through', value: toDisplayValue(p?.blowThrough) || 3, color: '#722ed1' },
                          { label: 'First Joint', value: p?.firstJoint || 'J-001', color: '#fff' },
                          { label: 'Last Joint', value: p?.lastJoint || 'J-085', color: '#fff' }
                        ];
                        return (
                          <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Metric</div>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>Value</div>
                            </div>
                            {rows.map((r, i) => (
                              <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '8px 16px', borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{r.label}</div>
                                <div style={{ color: r.color, fontSize: '13px', fontWeight: 500, textAlign: 'right' }}>{r.value}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Pipe Tally' ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(255, 20, 147, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AppstoreOutlined style={{ color: '#ff1493', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Pipe Tally</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Productivity (Weld Log) & pipe tally</div>
                  </div>
                  <Row gutter={[24, 16]}>
                    <Col xs={24} lg={12}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Today + cumulative by test type</div>
                      {(() => {
                        const ptData = [
                          { name: 'RT', today: toDisplayValue(dashboardMetrics?.pipeTally?.rt?.value) || 12, toDate: toDisplayValue(dashboardMetrics?.pipeTally?.rt?.toDate) || 245 },
                          { name: 'FJC', today: toDisplayValue(dashboardMetrics?.pipeTally?.fjc?.value) || 8, toDate: toDisplayValue(dashboardMetrics?.pipeTally?.fjc?.toDate) || 180 }
                        ];
                        return (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={ptData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                              <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Legend wrapperStyle={{ color: '#fff' }} />
                              <Bar dataKey="toDate" stackId="a" fill="rgba(255,20,147,0.35)" name="To Date" radius={[0, 0, 0, 0]} />
                              <Bar dataKey="today" stackId="a" fill="#ff1493" name="Today" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>
                    <Col xs={24} lg={12}>
                      <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Test Type</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Today</div>
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>To Date</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Radiographic Testing (RT)</div>
                          <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(dashboardMetrics?.pipeTally?.rt?.value) || 12}</div>
                          <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(dashboardMetrics?.pipeTally?.rt?.toDate) || 245}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '10px 16px' }}>
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Fit-up Joint Check (FJC)</div>
                          <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(dashboardMetrics?.pipeTally?.fjc?.value) || 8}</div>
                          <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(dashboardMetrics?.pipeTally?.fjc?.toDate) || 180}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Total Completed</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{toDisplayValue(dashboardMetrics?.pipeTally?.totalCompleted) || 425}</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>First Joint</div><div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{dashboardMetrics?.pipeTally?.firstJointWeld || 'J-001'}</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Last Joint</div><div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{dashboardMetrics?.pipeTally?.lastJointWeld || 'J-085'}</div></div>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Personnel' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Personnel Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(114, 46, 209, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TeamOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>PERSONNEL</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Personnel Entry</div>
                  </div>

                  <Row gutter={[16, 16]}>
                    {/* Left: Grouped bar chart (Planned vs Actual by role) */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const personnelSampleData = [
                          { name: 'Welders', planned: 12, actual: 10 },
                          { name: 'Fitters', planned: 8, actual: 7 },
                          { name: 'Helpers', planned: 6, actual: 6 },
                          { name: 'QC Inspector', planned: 3, actual: 2 },
                          { name: 'Supervisors', planned: 2, actual: 2 }
                        ];
                        const chartData = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? dashboardMetrics.personnel.entriesList.map(e => ({
                            name: e.name || e.keyName,
                            planned: bothShift ? (toDisplayValue(e.plannedDayShift) + toDisplayValue(e.plannedNightShift)) : (shift === 'day' ? toDisplayValue(e.plannedDayShift) : toDisplayValue(e.plannedNightShift)),
                            actual: bothShift ? (toDisplayValue(e.actualDayShift) + toDisplayValue(e.actualNightShift)) : (shift === 'day' ? toDisplayValue(e.actualDayShift) : toDisplayValue(e.actualNightShift))
                          }))
                          : personnelSampleData;
                        return (
                          <div style={{ marginBottom: '20px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Planned vs Actual by role</div>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} angle={-30} textAnchor="end" height={60} />
                                <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                                <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <Bar dataKey="planned" fill="#722ed1" name="Planned" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="actual" fill="#52c41a" name="Actual" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </Col>

                    {/* Right: Table + summary totals */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const personnelSampleData = [
                          { name: 'Welders', planned: 12, actual: 10 },
                          { name: 'Fitters', planned: 8, actual: 7 },
                          { name: 'Helpers', planned: 6, actual: 6 },
                          { name: 'QC Inspector', planned: 3, actual: 2 },
                          { name: 'Supervisors', planned: 2, actual: 2 }
                        ];
                        const tableData = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? dashboardMetrics.personnel.entriesList
                          : personnelSampleData.map((item, i) => ({
                            id: i,
                            keyName: item.name,
                            name: item.name,
                            plannedDayShift: item.planned,
                            plannedNightShift: 0,
                            actualDayShift: item.actual,
                            actualNightShift: 0
                          }));
                        const plannedTotal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.planned)
                          : tableData.reduce((s, e) => s + (e.plannedDayShift ?? e.planned ?? 0), 0);
                        const actualTotal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.actual)
                          : tableData.reduce((s, e) => s + (e.actualDayShift ?? e.actual ?? 0), 0);
                        const totalVal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.total)
                          : (plannedTotal + actualTotal);
                        return (
                          <>
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              marginBottom: '20px'
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Metric</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Planned</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Actual</div>
                              </div>
                              {tableData.map((entry, index) => (
                                <div
                                  key={entry.id ?? entry.keyName ?? index}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr',
                                    padding: '12px 16px',
                                    borderBottom: index < tableData.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                                  }}
                                >
                                  <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px', fontWeight: 400 }}>
                                    {entry.name || entry.keyName}
                                  </div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                                    {bothShift ? (toDisplayValue(entry.plannedDayShift) + toDisplayValue(entry.plannedNightShift)) : (shift === 'day' ? toDisplayValue(entry.plannedDayShift) : toDisplayValue(entry.plannedNightShift))}
                                  </div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                                    {bothShift ? (toDisplayValue(entry.actualDayShift) + toDisplayValue(entry.actualNightShift)) : (shift === 'day' ? toDisplayValue(entry.actualDayShift) : toDisplayValue(entry.actualNightShift))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              gap: '24px',
                              padding: '12px 0',
                              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Planned:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{plannedTotal}</span></div>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Actual:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{actualTotal}</span></div>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Total:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{totalVal}</span></div>
                            </div>
                          </>
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Consumables' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Consumables Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(82, 196, 26, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ShoppingOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>CONSUMABLES</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Consumables Entry · Trend: how much consumed vs remaining</div>
                  </div>

                  <Row gutter={[16, 16]}>
                    {/* Left: Horizontal bar chart (Consumed vs Remaining) */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const consumedVal = dashboardMetrics?.consumables?.toDate ?? 1250;
                        const remainingVal = dashboardMetrics?.consumables?.totalRemaining != null
                          ? dashboardMetrics.consumables.totalRemaining
                          : ((dashboardMetrics?.consumables?.entriesList || []).length > 0
                            ? (dashboardMetrics?.consumables?.entriesList || []).reduce((sum, e) => sum + Number(e.remaining ?? e.remainingStock ?? 0), 0)
                            : 3750);
                        const trendData = [
                          { name: 'Consumed (to date)', value: Number(consumedVal) || 1250, fill: '#1890ff' },
                          { name: 'Remaining', value: Number(remainingVal) || 3750, fill: '#52c41a' }
                        ];
                        return (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Consumption trend</div>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={trendData} layout="vertical" margin={{ left: 120, right: 20, top: 5, bottom: 5 }}>
                                <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                                <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} width={115} />
                                <Tooltip
                                  wrapperStyle={{ zIndex: 1000 }}
                                  contentStyle={{
                                    backgroundColor: '#2d3035',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '8px',
                                    color: '#ffffff',
                                    padding: '12px 16px',
                                    fontSize: '14px'
                                  }}
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const p = payload[0];
                                    const label = p.payload?.name ?? p.name ?? '';
                                    const value = p.value ?? p.payload?.value ?? 0;
                                    return (
                                      <div className="consumables-trend-tooltip" style={{ color: '#ffffff', fontSize: '14px' }}>
                                        <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '4px' }}>{label}</div>
                                        <div style={{ color: '#e8e8e8' }}>Qty: {toDisplayValue(value)}</div>
                                      </div>
                                    );
                                  }}
                                />
                                <Bar dataKey="value" name="Qty" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </Col>

                    {/* Right: Cost focus card first, then Table + Summary Totals */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const sampleTableData = [
                          { consumableName: 'Welding Electrode E7018', productCode: 'WE-7018', usedTodayDayShift: 45, usedTodayNightShift: 38, toDateValue: 850, remaining: 2150 },
                          { consumableName: 'Flux Core Wire', productCode: 'FC-71T', usedTodayDayShift: 12, usedTodayNightShift: 10, toDateValue: 220, remaining: 780 },
                          { consumableName: 'Shielding Gas (Argon)', productCode: 'SG-AR', usedTodayDayShift: 8, usedTodayNightShift: 6, toDateValue: 180, remaining: 820 },
                          { consumableName: 'Contact Tips', productCode: 'CT-35', usedTodayDayShift: 4, usedTodayNightShift: 3, toDateValue: 95, remaining: 405 },
                          { consumableName: 'Grinding Discs', productCode: 'GD-4.5', usedTodayDayShift: 6, usedTodayNightShift: 5, toDateValue: 105, remaining: 395 }
                        ];
                        const entriesList = dashboardMetrics?.consumables?.entriesList && dashboardMetrics.consumables.entriesList.length > 0
                          ? dashboardMetrics.consumables.entriesList
                          : sampleTableData;
                        const cost = dashboardMetrics?.consumables?.totalCost ?? dashboardMetrics?.consumables?.cost;
                        const costDisplay = cost != null && cost !== ''
                          ? (() => { const n = Number(toDisplayValue(cost)); return !Number.isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `$${toDisplayValue(cost)}`; })()
                          : '$12,450.00';
                        return (
                          <>
                            {/* Cost of Consumables – primary focus */}
                            <div style={{
                              marginBottom: '20px',
                              padding: '20px 24px',
                              background: 'linear-gradient(135deg, rgba(82, 196, 26, 0.25) 0%, rgba(82, 196, 26, 0.08) 100%)',
                              border: '2px solid rgba(82, 196, 26, 0.6)',
                              borderRadius: '12px',
                              boxShadow: '0 4px 16px rgba(82, 196, 26, 0.2)',
                              textAlign: 'center'
                            }}>
                              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.02em' }}>COST OF CONSUMABLES</div>
                              <div style={{ color: '#52c41a', fontSize: '32px', fontWeight: 800, letterSpacing: '0.02em' }}>{costDisplay}</div>
                              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '4px' }}>Total value of consumables (to date)</div>
                            </div>
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              marginBottom: '20px'
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Consumable Name</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Code</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Used Today</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>To Date</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Remaining</div>
                              </div>
                              {entriesList.map((entry, index) => (
                                <div
                                  key={entry.id || index}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                                    padding: '12px 16px',
                                    borderBottom: index < entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                                  }}
                                >
                                  <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px' }}>{entry.consumableName || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', textAlign: 'center' }}>{entry.productCode || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', textAlign: 'center' }}>
                                    {bothShift
                                      ? (toDisplayValue(entry.usedTodayDayShift ?? 0) + toDisplayValue(entry.usedTodayNightShift ?? 0))
                                      : (shift === 'day' ? toDisplayValue(entry.usedTodayDayShift ?? 0) : toDisplayValue(entry.usedTodayNightShift ?? 0))}
                                  </div>
                                  <div style={{ color: '#1890ff', fontSize: '14px', textAlign: 'center' }}>{toDisplayValue(entry.toDateValue ?? entry.usedToDate ?? 0)}</div>
                                  <div style={{ color: '#52c41a', fontSize: '14px', textAlign: 'center', fontWeight: 500 }}>{toDisplayValue(entry.remaining ?? entry.remainingStock ?? 0)}</div>
                                </div>
                              ))}
                            </div>

                            {/* Summary Totals */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 400 }}>Total Items</div>
                                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600, textAlign: 'right' }}>{toDisplayValue(dashboardMetrics?.consumables?.totalItems ?? entriesList.length)}</div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 400 }}>Used Today</div>
                                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600, textAlign: 'right' }}>{toDisplayValue(dashboardMetrics?.consumables?.usedToday ?? entriesList.reduce((sum, e) => sum + Number(e.usedTodayDayShift ?? 0) + Number(e.usedTodayNightShift ?? 0), 0))}</div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 400 }}>To Date</div>
                                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600, textAlign: 'right' }}>{toDisplayValue(dashboardMetrics?.consumables?.toDate ?? entriesList.reduce((sum, e) => sum + Number(e.toDateValue ?? e.usedToDate ?? 0), 0))}</div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 400 }}>Low Stock</div>
                                <div style={{ color: dashboardMetrics?.consumables?.lowStock > 0 ? '#faad14' : '#fff', fontSize: '16px', fontWeight: 600, textAlign: 'right' }}>{toDisplayValue(dashboardMetrics?.consumables?.lowStock ?? 1)}</div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 400 }}>Total Remaining</div>
                                <div style={{ color: '#52c41a', fontSize: '16px', fontWeight: 600, textAlign: 'right' }}>
                                  {dashboardMetrics?.consumables?.totalRemaining != null
                                    ? toDisplayValue(dashboardMetrics.consumables.totalRemaining)
                                    : toDisplayValue(entriesList.reduce((sum, e) => sum + Number(e.remaining ?? e.remainingStock ?? 0), 0))}
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
              ) : item.title === 'Production Impact' ? (
                <div>
                  {/* Production Impact Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(250, 173, 20, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      Production Impact
                    </Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Downtime Entry (responsibility split, delays)</div>
                  </div>

                  {/* 50/50 Layout: Donut Chart (left) + Detail Table + Summary (right) */}
                  <Row gutter={[16, 16]}>
                    {/* Left: Donut PieChart - downtime hours by responsibility */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const PIE_COLORS = ['#ff4d4f', '#faad14', '#1890ff', '#52c41a', '#722ed1', '#ff1493', '#13c2c2'];
                        const samplePieData = [
                          { name: 'Customer', value: 4.5 },
                          { name: 'Contractor', value: 2.0 },
                          { name: 'Equipment', value: 3.5 },
                          { name: 'Environment', value: 1.5 },
                          { name: 'Safety', value: 0.5 }
                        ];
                        let pieData = samplePieData;
                        if (dashboardMetrics?.productionImpact?.entriesList && dashboardMetrics.productionImpact.entriesList.length > 0) {
                          const categoryMap = {};
                          dashboardMetrics.productionImpact.entriesList.forEach(entry => {
                            if (entry.allocations && entry.allocations.length > 0) {
                              entry.allocations.forEach(a => {
                                const cat = a.category || 'Unallocated';
                                categoryMap[cat] = (categoryMap[cat] || 0) + (Number(a.hours) || 0);
                              });
                            } else {
                              const cat = entry.categoryTitle || 'Other';
                              categoryMap[cat] = (categoryMap[cat] || 0) + (Number(entry.totalHours) || 0);
                            }
                          });
                          pieData = Object.entries(categoryMap).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));
                        }
                        const totalHrs = pieData.reduce((s, d) => s + d.value, 0);
                        return (
                          <div style={{ marginBottom: '20px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Downtime hours by responsibility</div>
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie data={pieData} innerRadius={60} outerRadius={95} paddingAngle={2} dataKey="value" nameKey="name">
                                  {pieData.map((entry, idx) => <Cell key={`pi-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} formatter={(val) => `${val}h`} />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fill: '#fff', fontSize: '20px', fontWeight: 700 }}>{totalHrs.toFixed(1)}h</text>
                                <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fill: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>Total downtime</text>
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </Col>

                    {/* Right: Detail table + Summary totals */}
                    <Col xs={24} lg={12}>
                      {(() => {
                        const sampleTableData = [
                          { categoryTitle: 'Inspection Hold / QC Clearance', reason: 'Waiting for QC', startTime: '07:45', endTime: '12:15', totalHours: 4.5, allocations: [{ category: 'Customer', subCategory: 'QC Hold', hours: 4.5 }] },
                          { categoryTitle: 'Equipment Breakdown', reason: 'Welding machine failure', startTime: '14:00', endTime: '17:30', totalHours: 3.5, allocations: [{ category: 'Equipment', subCategory: 'Machine Breakdown', hours: 3.5 }] },
                          { categoryTitle: 'Safety Hold', reason: 'Permit not available', startTime: '09:00', endTime: '09:30', totalHours: 0.5, allocations: [{ category: 'Safety', subCategory: 'Permit', hours: 0.5 }] },
                          { categoryTitle: 'Weather Delay', reason: 'High wind', startTime: '11:00', endTime: '12:30', totalHours: 1.5, allocations: [{ category: 'Environment', subCategory: 'Wind', hours: 1.5 }] },
                          { categoryTitle: 'Re-welding Due to Defect', reason: 'Repair work', startTime: '15:00', endTime: '17:00', totalHours: 2.0, allocations: [{ category: 'Contractor', subCategory: 'Rework', hours: 2.0 }] }
                        ];
                        const tableData = dashboardMetrics?.productionImpact?.entriesList?.length > 0
                          ? dashboardMetrics.productionImpact.entriesList
                          : sampleTableData;
                        const incidentCount = dashboardMetrics?.productionImpact?.incidents != null ? toDisplayValue(dashboardMetrics.productionImpact.incidents) : tableData.length;
                        const totalHoursVal = dashboardMetrics?.productionImpact?.totalHours != null ? toDisplayValue(dashboardMetrics.productionImpact.totalHours) : tableData.reduce((s, e) => s + (Number(e.totalHours) || 0), 0);
                        const allocatedVal = dashboardMetrics?.productionImpact?.allocated != null ? toDisplayValue(dashboardMetrics.productionImpact.allocated) : tableData.reduce((s, e) => s + (Number(e.totalHours) || 0), 0);
                        return (
                          <>
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              marginBottom: '16px'
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1.2fr 0.8fr 0.8fr',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Category</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Reason</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Time</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Hours</div>
                              </div>
                              {tableData.map((entry, index) => (
                                <div
                                  key={entry.id || index}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.5fr 1.2fr 0.8fr 0.8fr',
                                    padding: '12px 16px',
                                    borderBottom: index < tableData.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px', fontWeight: 400 }}>{entry.categoryTitle || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>{entry.reason || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>{entry.startTime && entry.endTime ? `${entry.startTime}–${entry.endTime}` : '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>{toDisplayValue(entry.totalHours)}</div>
                                </div>
                              ))}
                            </div>
                            {/* Summary: Incidents, Total Hours, Allocated */}
                            <Row gutter={[12, 12]}>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Incidents</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{incidentCount}</div>
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Total Hours</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{totalHoursVal}</div>
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Allocated</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{allocatedVal}</div>
                                </div>
                              </Col>
                            </Row>
                          </>
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
              ) : (
                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px' }}>
                  {item.title} content will be displayed here.
                </div>
              )}
            </Card>
              </Col>
        ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default DashboardLog;

